import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { createAppTheme } from './index';

const STORAGE_KEY = 'admin_color_mode';

const getInitialMode = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'light' || saved === 'dark') return saved;
  } catch (e) {
    // localStorage may be unavailable (private mode / SSR) — fall through
  }
  return 'light';
};

const ColorModeContext = createContext({
  mode: 'light',
  toggleColorMode: () => {},
  setColorMode: () => {},
});

export const useColorMode = () => useContext(ColorModeContext);

export const ColorModeProvider = ({ children }) => {
  const [mode, setMode] = useState(getInitialMode);

  const persist = useCallback((next) => {
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch (e) {
      // ignore persistence errors
    }
  }, []);

  const setColorMode = useCallback((next) => {
    const value = next === 'dark' ? 'dark' : 'light';
    setMode(value);
    persist(value);
  }, [persist]);

  const toggleColorMode = useCallback(() => {
    setMode((prev) => {
      const next = prev === 'light' ? 'dark' : 'light';
      persist(next);
      return next;
    });
  }, [persist]);

  // Expose the mode to plain CSS (index.css) so non-MUI surfaces — e.g. the
  // ReactQuill editor and native scrollbars — can be themed for dark mode.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
  }, [mode]);

  const theme = useMemo(() => createAppTheme(mode), [mode]);

  const contextValue = useMemo(
    () => ({ mode, toggleColorMode, setColorMode }),
    [mode, toggleColorMode, setColorMode]
  );

  return (
    <ColorModeContext.Provider value={contextValue}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ColorModeContext.Provider>
  );
};

export default ColorModeContext;
